import { formatDateRange } from '@/utils';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetOfficeHourQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import CustomRangePicker from '../../../../../../components/CustomRangePicker';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

/* ------------------ TOOLTIP ------------------ */
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-[#0F172A] text-white px-4 py-3 rounded-xl border border-[#1f2d40] shadow-xl">
        <p className="mb-2 text-sm">{dayjs(label).format('MMM D, YYYY')}</p>

        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-[#38E1FF]" />
            Paid: {(payload[0].value / 60).toFixed(1)}h
          </div>

          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-[#FFC27A]" />
            Net: {(payload[1].value / 60).toFixed(1)}h
          </div>
        </div>
      </div>
    );
  }
  return null;
};

/* ------------------ COMPONENT ------------------ */
const OfficeHour = ({
  department_id,
  office_id,
  filter: allFilters,
  setFilter,
  isFilterChanged,
  resetFilter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
  setFilter: (key: any, value: any) => void;
  isFilterChanged: (key: any) => boolean;
  resetFilter: (key: any) => void;
}) => {
  const filter = allFilters.netOfficeHour;

  const { data: officeHourData, error } = useGetOfficeHourQuery({
    start_date: filter.start_date || '',
    end_date: filter.end_date || '',
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch office hour breakdown');

  const chartData = (officeHourData?.data?.data || []).map((item: any) => ({
    name: item.date,
    paid: item.total_scheduled_minutes || 0,
    net: item.total_worked_minutes || 0,
  }));

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full text-white">
      <Flex
        className="mb-4 text-white p-2"
        justify="space-between"
        align="center"
      >
        <h4 className="text-lg px-3 py-1 rounded border border-theme-gray-700 text-white leading-tight">
          Net Office Hour vs Paid Hour
          <span className="text-theme-primary block text-sm">
            {formatDateRange(filter.start_date, filter.end_date)}
          </span>
        </h4>

        <CustomRangePicker
          startDate={filter.start_date}
          endDate={filter.end_date}
          onChange={(start, end) => {
            setFilter('netOfficeHour', { start_date: start, end_date: end });
          }}
          disabledDate={(current) =>
            current > dayjs().subtract(1, 'day').endOf('day')
          }
          isChanged={isFilterChanged('netOfficeHour')}
          onReset={() => resetFilter('netOfficeHour')}
        />
      </Flex>

      <ResponsiveContainer width="100%" height={360}>
        <BarChart data={chartData} barGap={8}>
          <CartesianGrid
            strokeDasharray="4 4"
            stroke="#1E293B"
            vertical={false}
          />

          <XAxis
            dataKey="name"
            tick={{ fill: '#CBD5E1', fontSize: 13 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(val) => dayjs(val).format('MMM D')}
          />

          <YAxis
            tick={{ fill: '#CBD5E1', fontSize: 13 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${(v / 60).toFixed(0)}h`}
          />

          <Tooltip cursor={false} content={<CustomTooltip />} />

          <Bar
            dataKey="paid"
            fill="#38E1FF"
            radius={[6, 6, 0, 0]}
            barSize={18}
          />

          <Bar
            dataKey="net"
            fill="#FFC27A"
            radius={[6, 6, 0, 0]}
            barSize={18}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default OfficeHour;
